import { useGeolocation } from "@web/components/geo-protected-route";
import { useCallback, useState } from "react";
import type Webcam from "react-webcam";

const blobToDataUrl = (blob: Blob) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});

export function useCameraCapture(webcamRef: React.RefObject<Webcam | null>) {
	const { getCurrentCoordinates } = useGeolocation();

	const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
	const [showFlash, setShowFlash] = useState(false);

	const setCapturedImage = useCallback(
		(imageSrc: string) => {
			if (navigator.vibrate) navigator.vibrate(50);
			setShowFlash(true);
			getCurrentCoordinates();

			return new Promise<void>((resolve) => {
				setTimeout(() => {
					setCapturedImageUrl(imageSrc);
					setShowFlash(false);
					resolve();
				}, 200);
			});
		},
		[getCurrentCoordinates],
	);

	const capturePicture = useCallback(() => {
		const video = webcamRef.current?.video;
		if (!video) return;

		const canvas = document.createElement("canvas");
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		const context = canvas.getContext("2d");
		if (!context) return;

		context.drawImage(video, 0, 0, canvas.width, canvas.height);
		void setCapturedImage(canvas.toDataURL("image/jpeg", 1));
	}, [setCapturedImage, webcamRef]);

	const setCapturedImageBlob = useCallback(
		async (image: Blob) => {
			await setCapturedImage(await blobToDataUrl(image));
		},
		[setCapturedImage],
	);

	const resetCapture = () => {
		setCapturedImageUrl(null);
	};

	return {
		capturedImageUrl,
		showFlash,
		capturePicture,
		setCapturedImageBlob,
		resetCapture,
	};
}
